import {useContext, useState} from 'react';

import {Grid, IconButton, SxProps, Typography, Box} from '@mui/material';

import {DreamEmployerModal} from './DreamEmployerModal';
import {GradientIcon, InfoIcon, RibbonTrophyIcon} from '../../../../../../assets/employee_satisfaction_bar/index';
import EmpionDialog from '../../../../../../components/EmpionDialog/EmpionDialog';
import {LanguageContext} from '../../../../../../contexts/LanguageContext/LanguageContext';
import {greenColors, orangeColors} from '../../../../../../layouts/theme/designSystem';
import {DreamEmployerClass} from '../../types/DreamEmployerTypes';

interface Props {
  dreamEmployerData: DreamEmployerClass;
  isClickable?: boolean;
  backgroundColor?: string;
  fillColorGradientIcon?: string;
  fillColorRibbonIcon?: string;
  typographyStyles?: SxProps;
  iconSize?: {height: string; width: string};
  isPdf?: boolean;
  height?: string;
}

export const DreamEmployerBar = ({
  dreamEmployerData,
  isClickable = false,
  backgroundColor,
  fillColorGradientIcon,
  fillColorRibbonIcon,
  typographyStyles,
  iconSize = {height: '16px', width: '16px'},
  isPdf = false,
  height = '196px',
}: Props) => {
  const [open, setOpen] = useState(false);
  const currentLanguage = useContext(LanguageContext);

  const handleClose = () => setOpen(false);
  const handleOpen = () => setOpen(true);

  const gradientRightOffset = isPdf ? '-200px' : '-190px';
  const barBackgroundColor = backgroundColor || (orangeColors[4]);
  const gradientFillColor = fillColorGradientIcon || (orangeColors[4]);
  const ribbonFillColor = fillColorRibbonIcon || greenColors[700];

  return (
    <Grid container position='relative'>
      {/* Outer container */}
      <Grid item bgcolor={'white'} width='100%' height={height} borderRadius='16px' sx={{overflow: 'hidden'}}>
        {/* Inner bar */}
        <Grid
          item
          bgcolor={barBackgroundColor}
          width={isPdf ? '70%' : '80%'}
          height='100%'
          borderRadius='16px 0px 0px 16px'
          display='flex'
          justifyContent='flex-end'
          position='relative'
        >
          {/* Gradient Icon */}
          <GradientIcon
            fill={gradientFillColor}
            height={height}
            width={isPdf ? '275px' : '239px'}
            style={{
              position: 'absolute',
              right: gradientRightOffset,
            }}
          />
        </Grid>
      </Grid>

      {/* Content overlay */}
      <Grid
        container
        position='absolute'
        top={0}
        left={0}
        right={0}
        bottom={0}
        padding='12px 18px'
        justifyContent='space-between'
        alignItems='center'
        wrap='nowrap'
      >
        {/* Left content */}
        <Grid item display='flex' alignItems='flex-start' flex='1'>
          <RibbonTrophyIcon
            fill={ribbonFillColor}
            height={iconSize.height}
            width={iconSize.width}
            style={{
              marginRight: '12px',
            }}
          />
          <Box sx={{maxWidth: isPdf ? '65%' : '80%'}}>
            <Typography
              variant='h3'
              sx={{
                color: greenColors[900],
                ...typographyStyles,
                lineHeight: 1,
                fontSize: '24px',
              }}
              gutterBottom
            >
              {dreamEmployerData.headline[currentLanguage]}
            </Typography>
            <Typography
              sx={{
                color: greenColors[900],
                ...typographyStyles,
                fontSize: isPdf ? '14px' : '20px',
                lineHeight: 1.2,
              }}
            >
              {dreamEmployerData.text[currentLanguage]}
            </Typography>
          </Box>
        </Grid>

        {/* Info button */}
        {isClickable && (
          <Grid item>
            <IconButton onClick={handleOpen}>
              <InfoIcon fill={greenColors[700]} height={24} width={24} />
            </IconButton>
            <EmpionDialog open={open} onClose={handleClose} maxWidth='sm'>
              <Typography variant='h2' mb={2}>
                <DreamEmployerModal dreamEmployerData={dreamEmployerData} handleClose={handleClose} />
              </Typography>
            </EmpionDialog>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};
